import Modal from 'bootstrap/js/src/modal'
import { component, computed, observable, subscribe } from 'knockout-decorators'

import source from './event-box.html?raw'
import './event-box.less'
import './offer-list'

import { Background, ImageProcessStep } from '~/pages/background'
import { EventState } from '~/store/event'
import { CartState } from '../cart/state'
import { Language, Translations } from '~/utils/language'

import ErrorIcon from '~/images/status-error.svg?raw'
import GalleryIcon from '~/images/image-gallery.svg?raw'
import { EventSaleStatus } from '@tixa/schema'

@component('event-box', source)
export class EventBox {
  private eventState: EventState = EventState.get()
  private cartState: CartState = CartState.get()

  @computed get hasTickets(): boolean {
    return this.eventState.hasTickets
  }

  @computed get canBuy(): boolean {
    return this.eventState.canBuy
  }

  @computed get showHandlingFee(): boolean {
    return !this.eventState.allTicketsFree && this.eventState.hasHandlingFee
  }

  @computed get handlingFee(): string {
    const fee = this.eventState.handlingFee
    return Language.formatCurrency(fee, this.eventState.offers[0].priceCurrency)
  }

  @computed get buyCtaText(): string {
    if (this.eventState.offers.some((offer) => offer.price > 0)) {
      return Translations.buy
    }
    return Translations.reserveticket
  }

  @computed get showCart(): boolean {
    return this.eventState.cartRequest !== null || this.cartState.hasCartExpired
  }

  @computed get remainingTime(): string {
    return this.eventState.remainingTime
  }

  @computed get showExpireNotice(): boolean {
    return (
      !!this.eventState.eventConfigs.displayExpiryNotice &&
      !this.eventState.hasExpired
    )
  }

  @computed get expiredWithoutCart(): boolean {
    return this.eventState.hasExpired && !this.eventState.cartRequest
  }

  @computed get isCartLoading(): boolean {
    return (
      this.cartState.isPrechecking ||
      (this.cartState.request !== null &&
        !this.cartState.isRequestOutdated &&
        this.cartState.content === null)
    )
  }

  @computed get formatedExpireTime(): string {
    const alreadyFormatedTime = this.eventState.remainingTime
    if (alreadyFormatedTime.length > 0) {
      return Translations.dynamicpricingtime.replace('%', alreadyFormatedTime)
    }
    if (this.eventState.eventConfigs.displayExpiryTime !== undefined) {
      const minutes = Math.floor(
        this.eventState.eventConfigs.displayExpiryTime / 60000
      )
      return Translations.dynamicpricingtime.replace('%', minutes)
    }
    return ''
  }

  @observable public name = ''
  @observable public locationName = ''
  @observable public locationUrl = ''

  @observable public locationLogoUrl: string | null = null
  @observable public coverUrl = ''
  @observable public hasGallery = false

  @observable public startDate = ''
  @observable public doorTime = ''
  @observable public hasDoorTime = false

  @observable public isSoldout = false
  @observable public soldoutMessage = ''

  @observable public errorMessage = ''
  private ImageBg = Background.getInstance()

  private errorIcon = ErrorIcon
  private galleryIcon = GalleryIcon

  private errorModal: Modal = null
  private imageModal: Modal = null
  private hasFirstImageLoaded = false

  private events = this.eventState.events

  @observable public hasCodeApplied = false

  @observable public showFloatingBuy = false
  private floatingBuyObserver: IntersectionObserver | null = null
  private sideElement: HTMLElement | null = null
  private readonly mobileMaxWidth = 1000

  constructor() {
    /*subscribe<Promise<CartResponse>>(
      () => State.cartRequest,
      (newRq) => {
        if (!newRq) {
          window.scrollTo({ behavior: 'smooth', top: 0 })
        }
      }
    )*/
    subscribe<string>(
      () => this.eventState.globalError,
      (newError) => {
        if (newError.length > 0) {
          this.openError(newError)
        }
      }
    )
    const event = this.eventState.event
    if (!event) {
      return
    }
    this.name = event.name
    this.locationName = event.location.name
    this.locationUrl = event.location.url
    this.locationLogoUrl = event.location.logo || null
    this.startDate = event.customDate || event.startDate

    if (event.doorTime) {
      this.doorTime = event.doorTime
      this.hasDoorTime = true
    }

    this.coverUrl = event.image[0]
    this.hasGallery = event.image.length > 1

    if (this.eventState.eventConfigs.displayExpiryNotice) {
      setTimeout(() => {
        this.checkIfExpired()
      }, this.eventState.eventConfigs.displayExpiryTime)
    }

    subscribe<string[]>(
      () => this.eventState.specialOffers.codeApplied,
      (newCodeApplied) => {
        this.hasCodeApplied = newCodeApplied.length > 0
      }
    )
  }

  private checkIfExpired() {
    if (!this.eventState.cartRequest) {
      this.eventState.hasExpired = true
    } else {
      setTimeout(() => {
        this.checkIfExpired()
      }, 500)
    }
  }

  public openError(message: string): void {
    this.errorMessage = message
    this.errorModal.show()
  }

  private initErrorModal(element: HTMLElement) {
    this.errorModal = new Modal(element, {
      backdrop: 'static',
      focus: true,
      keyboard: false,
      show: true,
    })
  }

  private hideErrorModal(): void {
    this.errorMessage = ''
    this.eventState.globalError = ''
    if (this.errorModal) {
      this.errorModal.hide()
    }
  }

  private initImageModal(element: HTMLElement) {
    this.imageModal = new Modal(element, {
      backdrop: true,
      focus: true,
      keyboard: true,
    })
  }

  private openImageModal(): void {
    if (window.matchMedia('(max-device-width: 1000px)').matches) {
      return
    }
    if (this.imageModal) {
      this.imageModal.show()
    }
  }

  private closeImageModal(): void {
    if (this.imageModal) {
      this.imageModal.hide()
    }
  }

  private stopPropagation(_data: unknown, event: Event): boolean {
    event.stopPropagation()
    return true
  }

  private async imageLoaded(image: HTMLImageElement) {
    if (!this.hasFirstImageLoaded) {
      this.hasFirstImageLoaded = true
      await this.ImageBg.addCanvasBackground(this.coverUrl, image, [
        ImageProcessStep.original,
        ImageProcessStep.colorOverlay,
        ImageProcessStep.blur,
        ImageProcessStep.shadow,
      ])
      this.ImageBg.setCanvasBackground(this.coverUrl)
    }
  }

  private reload() {
    window.location.reload()
  }

  private async buyClicked() {
    if (this.isCartLoading) {
      return
    }
    await this.eventState.submitter.buy(this.hasTouristTax)
  }

  @computed get isBuyNowDisabled(): boolean {
    return (
      !this.hasCodeApplied &&
      this.events.some((event) => event.saleStatus === EventSaleStatus.codeonly)
    )
  }

  @computed get hasTouristTax(): boolean {
    return this.events.some((event) => event.hasTouristTax === true)
  }

  scrollToTickets(): void {
    this.sideElement?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  initFloatingBuySentinel(element: HTMLElement): void {
    this.sideElement = element
    if (!window.matchMedia(`(max-width: ${this.mobileMaxWidth}px)`).matches) {
      return
    }
    this.floatingBuyObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        const isAboveViewport = entry.boundingClientRect.bottom < 0
        this.showFloatingBuy = !entry.isIntersecting && isAboveViewport
      },
      { threshold: 0, rootMargin: '0px' }
    )
    this.floatingBuyObserver.observe(element)
  }
}
